import { Admin, Authenticated, CustomRoutes, Resource } from 'react-admin';
import ContactList from './contacts/ContactList';
import ContactEdit from './contacts/ContactEdit';
import ContactCreate from './contacts/ContactCreate';
import ContactShow from './contacts/ContactShow.tsx';
import GroupList from './groups/GroupList.tsx';
import GroupEdit from './groups/GroupEdit.tsx';
import GroupCreate from './groups/GroupCreate.tsx';
import GroupShow from './groups/GroupShow.tsx';
import OpportunityList from './opportunities/OpportunityList.tsx';
import ProgramList from './programs/ProgramList.tsx';
import ProgramCreate from './programs/ProgramCreate.tsx';
import ProgramEdit from './programs/ProgramEdit.tsx';
import OpportunityCreate from './opportunities/OpportunityCreate.tsx';
import OpportunityEdit from './opportunities/OpportunityEdit.tsx';
import OpportunityShow from './opportunities/OpportunityShow.tsx';
import { Route } from 'react-router-dom';
import DashboardPage from './dashboard/Dashboard.tsx';
import ProjectList from './projects/ProjectList.tsx';
import ProjectEdit from './projects/ProjectEdit.tsx';
import ProjectShow from './projects/ProjectShow.tsx';
import ProjectCreate from './projects/ProjectCreate.tsx';
import ProjectContactEdit from './project-contacts/ProjectContactEdit.tsx';
import { authProvider } from './authProvider.ts';
import dataProvider from './dataProvider.ts';
import CustomLayout from './CustomLayout.tsx';
import ReportsLandingPage from './reports/ReportsLandingPage.tsx';
import PipelineReportPage from './reports/PipelineReportPage.tsx';
import WonReportPage from './reports/WonReportPage.tsx';
import ContactsReportPage from './reports/ContactsReportPage.tsx';
import CompanyList from './companies/CompanyList.tsx';
import CompanyEdit from './companies/CompanyEdit.tsx';
import CompanyCreate from './companies/CompanyCreate.tsx';
import CompanyShow from './companies/CompanyShow.tsx';
import IntroductionsReportPage from './reports/IntroductionsReportPage.tsx';
import LostButTrackPage from './reports/LostButTrackPage.tsx';
import EventsReportPage from './reports/EventsReportPage.tsx';
import GroupEventEdit from './events/GroupEventEdit.tsx';
import IndustryList from './industry/IndustryList.tsx';
import IndustryEdit from './industry/IndustryEdit.tsx';
import IndustryCreate from './industry/IndustryCreate.tsx';
import OpportunityProfileEdit from './opportunities/OpportunityProfileEdit.tsx';
import OpportunityProfileCreate from './opportunities/OpportunityProfileCreate.tsx';
import OpportunityProfileList from './opportunities/OpportunityProfileList.tsx';
import GroupContactSuggestionList from './group-contact-suggestions/GroupContactSuggestionList.tsx';
import ContactSuggestionList from './contact-suggestions/ContactSuggestionList.tsx';
import SettingsPage from './settings/SettingsPage.tsx';
import { forgeTheme } from './theme.ts';
import ForgeLogin from './ForgeLogin.tsx';
import DemoStart from './demo/DemoStart.tsx';
import WelcomePage from './signup/WelcomePage.tsx';
import SetPasswordPage from './signup/SetPasswordPage.tsx';
import RenewalsPage from './renewals/RenewalsPage.tsx';
import DataImportPage from './data-import/DataImportPage.tsx';
import PlatformTenantsPage from './platform/PlatformTenantsPage.tsx';
import PlatformTenantPage from './platform/PlatformTenantPage.tsx';

export const AppAdmin = () => (
  <Admin
    dataProvider={dataProvider}
    dashboard={DashboardPage}
    authProvider={authProvider}
    loginPage={ForgeLogin}
    theme={forgeTheme}
    layout={CustomLayout}
  >
    <CustomRoutes noLayout>
      {/* Public: the marketing site's "Try the demo" lands here. */}
      <Route path="/demo" element={<DemoStart />} />
      {/* Public: Stripe sends a buyer here after paying, and the welcome email links here. */}
      <Route path="/welcome" element={<WelcomePage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
    </CustomRoutes>
    <CustomRoutes>
      <Route
        path="/"
        element={
          <Authenticated>
            <DashboardPage />
          </Authenticated>
        }
      />
      <Route
        path="/"
        element={
          <Authenticated>
            <DashboardPage />
          </Authenticated>
        }
      />
      <Route
        path="/reports"
        element={
          <Authenticated>
            <ReportsLandingPage />
          </Authenticated>
        }
      />
      <Route
        path="/reports/pipeline"
        element={
          <Authenticated>
            <PipelineReportPage />
          </Authenticated>
        }
      />
      <Route
        path="/reports/introductions"
        element={
          <Authenticated>
            <IntroductionsReportPage />
          </Authenticated>
        }
      />

      <Route
        path="/reports/won"
        element={
          <Authenticated>
            <WonReportPage />
          </Authenticated>
        }
      />
      <Route
        path="/reports/contacts"
        element={
          <Authenticated>
            <ContactsReportPage />
          </Authenticated>
        }
      />

      <Route
        path="/reports/lost-but-tracked"
        element={
          <Authenticated>
            <LostButTrackPage />
          </Authenticated>
        }
      />
      <Route
        path="/reports/events"
        element={
          <Authenticated>
            <EventsReportPage />
          </Authenticated>
        }
      />
      <Route
        path="/settings"
        element={
          <Authenticated>
            <SettingsPage />
          </Authenticated>
        }
      />
      {/* Platform admins only; the API answers 404 for anyone else. */}
      <Route
        path="/platform"
        element={
          <Authenticated>
            <PlatformTenantsPage />
          </Authenticated>
        }
      />
      <Route
        path="/platform/tenants/:id"
        element={
          <Authenticated>
            <PlatformTenantPage />
          </Authenticated>
        }
      />
      <Route
        path="/renewals"
        element={
          <Authenticated>
            <RenewalsPage />
          </Authenticated>
        }
      />
      <Route
        path="/settings/import"
        element={
          <Authenticated>
            <DataImportPage />
          </Authenticated>
        }
      />
      <Route
        path="/settings/import/:kind"
        element={
          <Authenticated>
            <DataImportPage />
          </Authenticated>
        }
      />
    </CustomRoutes>
    <Resource
      name="companies"
      list={CompanyList}
      edit={CompanyEdit}
      create={CompanyCreate}
      show={CompanyShow}
    />

    <Resource
      name="contacts"
      list={ContactList}
      edit={ContactEdit}
      create={ContactCreate}
      show={ContactShow}
      recordRepresentation={(record) => {
        const firstName = typeof record?.firstName === 'string' ? record.firstName.trim() : '';
        const lastName = typeof record?.lastName === 'string' ? record.lastName.trim() : '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) return fullName;
        return record?.id ? `Contact #${record.id}` : 'Contact';
      }}
    />
    <Resource name="industry" list={IndustryList} edit={IndustryEdit} create={IndustryCreate} />
    <Resource
      name="opportunity-profiles"
      list={OpportunityProfileList}
      edit={OpportunityProfileEdit}
      create={OpportunityProfileCreate}
    />
    <Resource
      name="groups"
      list={GroupList}
      edit={GroupEdit}
      create={GroupCreate}
      show={GroupShow}
    />
    <Resource
      name="group-contact-suggestions"
      list={GroupContactSuggestionList}
      options={{ label: 'Group Suggestions' }}
    />
    <Resource
      name="contact-suggestions"
      list={ContactSuggestionList}
      options={{ label: 'Email Suggestions' }}
    />
    <Resource name="group-events" edit={GroupEventEdit} />

    <Resource
      name="opportunities"
      list={OpportunityList}
      edit={OpportunityEdit}
      create={OpportunityCreate}
      show={OpportunityShow}
    />
    <Resource
      name="projects"
      list={ProjectList}
      show={ProjectShow}
      edit={ProjectEdit}
      create={ProjectCreate}
    />
    <Resource name="project-contacts" edit={ProjectContactEdit} />
    <Resource name="programs" list={ProgramList} edit={ProgramEdit} create={ProgramCreate} />
  </Admin>
);
